import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import {
  CommentarySourceId,
  CommentaryChapter,
  ParsedPassage,
  COMMENTARY_SOURCES,
  fetchHelloAOCommentary,
  fetchGuzikCommentary,
  fetchChuckSmithCommentary,
  fetchRobertsonCommentary,
} from '../lib/commentary';

interface UseCommentaryOptions {
  passage: ParsedPassage | null;
  sourceId: CommentarySourceId;
}

interface UseCommentaryReturn {
  data: CommentaryChapter | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useCommentary({ passage, sourceId }: UseCommentaryOptions): UseCommentaryReturn {
  const [data, setData] = useState<CommentaryChapter | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const doFetch = useCallback(async () => {
    if (!passage) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    try {
      let result: CommentaryChapter;
      const sourceMeta = COMMENTARY_SOURCES.find(s => s.id === sourceId)!;

      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) throw new Error('Sign in to view this commentary.');

      if (sourceMeta.provider === 'helloao') {
        result = await fetchHelloAOCommentary(
          sourceId as Exclude<CommentarySourceId, 'guzik' | 'smith' | 'robertson'>,
          passage.bookId,
          passage.chapter,
          import.meta.env.VITE_SUPABASE_URL,
          sessionData.session.access_token,
          import.meta.env.VITE_SUPABASE_ANON_KEY
        );
      } else {
        if (sourceId === 'smith') {
          result = await fetchChuckSmithCommentary(
            passage.bookId,
            passage.chapter,
            import.meta.env.VITE_SUPABASE_URL,
            sessionData.session.access_token,
            import.meta.env.VITE_SUPABASE_ANON_KEY
          );
        } else if (sourceId === 'robertson') {
          result = await fetchRobertsonCommentary(
            passage.bookId,
            passage.chapter,
            import.meta.env.VITE_SUPABASE_URL,
            sessionData.session.access_token,
            import.meta.env.VITE_SUPABASE_ANON_KEY
          );
        } else {
          result = await fetchGuzikCommentary(
            passage.bookId,
            passage.chapter,
            import.meta.env.VITE_SUPABASE_URL,
            sessionData.session.access_token,
            import.meta.env.VITE_SUPABASE_ANON_KEY
          );
        }
      }

      if (requestId === requestIdRef.current) {
        setData(result);
        setError(null);
      }
    } catch (err) {
      if (requestId === requestIdRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to load commentary.');
        setData(null);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [passage?.bookId, passage?.chapter, sourceId]);

  useEffect(() => {
    doFetch();
  }, [doFetch]);

  return { data, loading, error, refetch: doFetch };
}
